// @flow
import gql from 'graphql-tag';
import React from 'react';
import Helmet from 'react-helmet-async';
import {injectIntl, type IntlShape} from 'react-intl';
import {compose} from 'redux';
import {connect} from 'react-redux';

import type {State as ReduxState} from '../../reducers/index';
import type {FacebookData} from '../../../server/utils/systemData';
import {makePageURL} from '../../utils/routerUtils';

export type AlternativeLink = {
    link: string,
    hrefLang: string
};

type StateProps = {
    brand: string,
    domainName: string,
    hostname: string,
    siteName: string,
    facebook: FacebookData,
    pathname?: string,
    searchParams: *,
    nextPageUrl?: string,
    prevPageUrl?: string
};

type Props = {
    intl: IntlShape,
    metaDescription?: string,
    metaKeywords?: string,
    pageTitle?: string,
    canonicalLink?: string,
    alternativeLinks?: AlternativeLink[],
    page?: number,
    pageCount?: number
} & StateProps;

const MESSAGE_DEFINITION = {
    fallback: {
        id: 'page.title.fallback',
        defaultMessage: 'Vacation Rentals',
        description: 'page title'
    }
};

export const fragments = {
    results: gql`
        fragment HtmlTitleResults on Results {
            pageTitle
            metaDescription
            metaKeywords
            canonicalLink(url: $url)
            alternativeLinks(url: $url) {
                link
                hrefLang
            }
        }
    `
};

export const HtmlTitle = (props: Props) => {
    const {
        domainName,
        hostname,
        siteName,
        facebook,
        intl: {formatMessage},
        pathname,
        pageTitle,
        metaDescription,
        metaKeywords,
        canonicalLink,
        alternativeLinks,
        page = 0,
        pageCount = 0,
        nextPageUrl,
        prevPageUrl
    } = props;

    const hasPrevUrl = nextPageUrl && page > 1;
    const hasNextUrl = prevPageUrl && page < pageCount;
    const htmlTitleText =
        pageTitle || formatMessage(MESSAGE_DEFINITION.fallback);

    return (
        <Helmet defaultTitle="Vacation Rentals">
            <title>{htmlTitleText}</title>
            {canonicalLink ? (
                <link rel="canonical" href={canonicalLink} />
            ) : null}
            {metaKeywords ? (
                <meta name="keywords" content={metaKeywords} />
            ) : null}
            {metaDescription ? (
                <meta name="description" content={metaDescription} />
            ) : null}
            <meta property="fb:admins" content={facebook.admins} />
            <meta property="fb:app_id" content={facebook.appId} />
            <meta property="og:type" content="company" />
            <meta property="og:title" content={htmlTitleText} />
            <meta
                property="og:url"
                content={`https://${hostname}${pathname ? pathname : ''}`}
            />
            <meta
                property="og:image"
                content={`https://csvcus.homeaway.com/rsrcs/cdn-logos/2.4.4/bce/moniker/${siteName}/logo.png`}
            />
            <meta property="og:site_name" content={domainName} />
            <meta property="og:locale" content={facebook.locale} />
            {hasPrevUrl && <link rel={'prev'} href={prevPageUrl} />}
            {hasNextUrl && <link rel={'next'} href={nextPageUrl} />}
            {alternativeLinks &&
                alternativeLinks.map(({link, hrefLang}) => (
                    <link
                        key={link}
                        hrefLang={hrefLang}
                        href={link}
                        rel={'alternate'}
                    />
                ))}
        </Helmet>
    );
};

const mapStateToProps = (state: ReduxState, {page = 0}): StateProps => {
    const {
        system: {brand, domainName, hostname, siteName, facebook},
        searchParams: {params},
        router: {pathname}
    } = state;

    return {
        brand,
        domainName,
        hostname,
        siteName,
        facebook,
        pathname,
        searchParams: params,
        nextPageUrl: makePageURL(page + 1, state),
        prevPageUrl: makePageURL(page - 1, state)
    };
};

export default compose(
    connect(mapStateToProps),
    injectIntl
)(HtmlTitle);
